import { useState } from 'react'
import { Plus, UserRound, Edit3, Trash2, Lock, Sparkles, Volume2 } from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { MioLeadForm } from './MioLeadForm'
import { useMieiLead } from '../../hooks/useMieiLead'
import { useVociElevenLabs } from '../../hooks/useVociElevenLabs'
import { useUpgrade } from '../../hooks/useUpgrade'
import { useToast } from '../../stores/toastStore'
import { useAuthStore } from '../../stores/authStore'
import { etichettaDifficolta, varianteDifficolta } from '../../lib/format'
import type { LeadAdmin } from '../../lib/tipi'
import pagina from '../pagine.module.css'
import styles from './MieiLead.module.css'

/**
 * Pagina "I miei Lead" per l'SDR.
 *
 * Comportamento per piano:
 *  - Free → banner upgrade, nessuna lista
 *  - Pro → lista + barra progresso X/10 (colore varia 0-6 verde, 7-9 arancio, 10 rosso)
 *  - Team → lista vuota o personale (team leads sono gestiti dal manager)
 *  - Enterprise → lista illimitata, nessuna barra progressione
 */
export function MieiLead() {
  const { utente } = useAuthStore()
  const toast = useToast()
  const { avviaUpgrade, vaiAbbonamento, caricamento: caricamentoUpgrade, errore: erroreUpgrade } = useUpgrade()
  const {
    leads, totale, limite, disponibili, piano, puoCreare,
    isLoading, elimina,
  } = useMieiLead()
  // Carichiamo le voci ElevenLabs in background per mostrare il nome anziché il voice_id
  const { voci, caricaVoci, trovaNomeVoce } = useVociElevenLabs()
  // Trigger lazy: solo dopo che la lista è caricata
  if (leads.length > 0 && voci.length === 0) {
    void caricaVoci()
  }

  const [drawerAperto, setDrawerAperto] = useState(false)
  const [leadInModifica, setLeadInModifica] = useState<LeadAdmin | null>(null)
  const [leadInEliminazione, setLeadInEliminazione] = useState<LeadAdmin | null>(null)

  // ─── Piano Free: banner upgrade ──────────────────────────────────
  if (utente?.piano === 'free') {
    return (
      <div className={pagina.wrap}>
        <div className={pagina.header}>
          <div>
            <h1 className={pagina.titolo}>I miei Lead</h1>
            <div className={pagina.sottotitolo}>I tuoi profili lead personalizzati.</div>
          </div>
        </div>
        <Card>
          <div className={styles.upgrade}>
            <div className={styles.upgradeIcona}>
              <Lock size={32} />
            </div>
            <h2>I lead personalizzati sono disponibili dal piano Pro</h2>
            <p>
              Con il piano <strong>Pro</strong> puoi creare fino a <strong>10 lead custom</strong> con
              voci personalizzate, obiezioni e obiettivi su misura.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 16 }}>
              <Button
                variante="primary"
                iconaSinistra={<Sparkles size={14} />}
                caricamento={caricamentoUpgrade === 'pro'}
                disabled={caricamentoUpgrade !== null}
                onClick={() => avviaUpgrade('pro')}
              >
                {caricamentoUpgrade === 'pro' ? 'Reindirizzamento…' : 'Passa a Pro — 7 giorni gratis'}
              </Button>
              {erroreUpgrade && (
                <div style={{ fontSize: 13, color: 'var(--danger)' }}>{erroreUpgrade}</div>
              )}
            </div>
          </div>
        </Card>
      </div>
    )
  }

  if (isLoading) return <div className={pagina.skeleton}>Caricamento…</div>

  const limiteRaggiunto = limite !== null && totale >= limite

  return (
    <div className={pagina.wrap}>
      {/* Header */}
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>I miei Lead</h1>
          <div className={pagina.sottotitolo}>
            I tuoi profili lead personalizzati per allenarti su situazioni specifiche.
          </div>
        </div>
        <Button
          iconaSinistra={<Plus size={16} />}
          disabled={!puoCreare}
          onClick={() => { setLeadInModifica(null); setDrawerAperto(true) }}
          title={!puoCreare ? `Hai raggiunto il limite di ${limite} lead.` : undefined}
        >
          Nuovo Lead
        </Button>
      </div>

      {/* Progress bar — solo se c'è un limite definito (Pro) */}
      {limite != null && (
        <BarraProgressione totale={totale} limite={limite} />
      )}

      {/* Banner upgrade quando limite raggiunto */}
      {limiteRaggiunto && (
        <Card>
          <div className={styles.banner}>
            <div>
              <strong>⚠ Hai raggiunto il limite di {limite} lead del piano {piano === 'pro' ? 'Pro' : piano}.</strong>
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
                Elimina un lead esistente o passa a Enterprise per lead illimitati.
              </div>
            </div>
            <Button
              variante="primary"
              iconaSinistra={<Sparkles size={14} />}
              onClick={vaiAbbonamento}
            >
              Scopri Enterprise
            </Button>
          </div>
        </Card>
      )}

      {/* Lista lead o stato vuoto */}
      {leads.length === 0 ? (
        <EmptyState
          icona={<UserRound size={40} />}
          titolo="Nessun lead ancora"
          descrizione="Crea il tuo primo profilo lead personalizzato per allenarti su situazioni specifiche del tuo settore."
          azione={
            <Button
              iconaSinistra={<Plus size={14} />}
              onClick={() => { setLeadInModifica(null); setDrawerAperto(true) }}
              disabled={!puoCreare}
            >
              Crea il primo lead
            </Button>
          }
        />
      ) : (
        <div className={styles.lista}>
          {leads.map((l) => (
            <Card key={l.id}>
              <div className={styles.cardCorpo}>
                <AvatarLead nome={l.nome} />
                <div className={styles.cardCentrale}>
                  <div className={styles.cardNome}>{l.nome}</div>
                  <div className={styles.cardMeta}>
                    {l.ruolo} · {l.settore}
                  </div>
                  <div className={styles.cardBadges}>
                    <Badge variante={varianteDifficolta(l.difficolta)}>
                      {etichettaDifficolta(l.difficolta)}
                    </Badge>
                    {l.obiettivoChiamata && (
                      <span className={pagina.tag}>🎯 {l.obiettivoChiamata}</span>
                    )}
                  </div>
                  <div className={styles.cardMetaSecondaria}>
                    <span className={styles.voce}>
                      <Volume2 size={12} />
                      {l.voiceId
                        ? (trovaNomeVoce(l.voiceId) ?? <code>{l.voiceId.slice(0, 8)}…</code>)
                        : 'Voce default'}
                    </span>
                    {l.interno != null && (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        · Interno {l.interno}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.cardAzioni}>
                  <Button
                    variante="secondary"
                    taglia="sm"
                    iconaSinistra={<Edit3 size={13} />}
                    onClick={() => { setLeadInModifica(l); setDrawerAperto(true) }}
                  >
                    Modifica
                  </Button>
                  <Button
                    variante="ghost"
                    taglia="sm"
                    onClick={() => setLeadInEliminazione(l)}
                  >
                    <Trash2 size={13} color="var(--danger)" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Drawer crea/modifica */}
      <MioLeadForm
        aperto={drawerAperto}
        lead={leadInModifica}
        rimanenti={disponibili}
        onChiudi={() => setDrawerAperto(false)}
        onSalvato={() => setDrawerAperto(false)}
      />

      {/* Modale conferma eliminazione */}
      <Modal
        aperto={!!leadInEliminazione}
        titolo="Eliminare il lead?"
        onChiudi={() => setLeadInEliminazione(null)}
      >
        <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
          Sei sicuro di voler eliminare <strong>{leadInEliminazione?.nome}</strong>?
          Se hai già fatto sessioni con questo lead, lo storico verrà conservato ma il lead non sarà più disponibile.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variante="ghost" onClick={() => setLeadInEliminazione(null)}>Annulla</Button>
          <Button
            variante="danger"
            caricamento={elimina.isPending}
            onClick={async () => {
              if (!leadInEliminazione) return
              const nome = leadInEliminazione.nome
              try {
                await elimina.mutateAsync(leadInEliminazione.id)
                toast.successo(`Lead "${nome}" eliminato`)
                setLeadInEliminazione(null)
              } catch {
                toast.errore('Errore eliminazione lead')
              }
            }}
          >
            Elimina
          </Button>
        </div>
      </Modal>
    </div>
  )
}

// ─── Sotto-componenti ────────────────────────────────────────────

function BarraProgressione({ totale, limite }: { totale: number; limite: number }) {
  const pct = Math.min(100, (totale / limite) * 100)
  const sogliaArancio = Math.ceil(limite * 0.7) // 7/10 per limite 10
  const colore = totale >= limite ? 'var(--danger)'
    : totale >= sogliaArancio ? 'var(--warning)'
    : 'var(--success)'

  return (
    <div className={styles.barraWrap}>
      <div className={styles.barraTrack}>
        <div className={styles.barraFill} style={{ width: `${pct}%`, background: colore }} />
      </div>
      <div className={styles.barraTesto}>
        <strong style={{ color: colore }} className="mono">{totale} / {limite}</strong>
        <span style={{ color: 'var(--text-secondary)' }}> lead usati</span>
      </div>
    </div>
  )
}

function AvatarLead({ nome }: { nome: string }) {
  const iniziali = nome.split(/\s+/).map((p) => p[0] ?? '').slice(0, 2).join('').toUpperCase() || '?'
  let h = 0
  for (let i = 0; i < nome.length; i++) h = (h * 31 + nome.charCodeAt(i)) >>> 0
  const colori = ['#6C47FF', '#16A34A', '#2563EB', '#D97706', '#DC2626', '#0EA5E9']
  return (
    <div className={styles.avatar} style={{ background: colori[h % colori.length] }}>
      {iniziali}
    </div>
  )
}
